import type { ProjectEntity } from '../../domain/entities/ProjectEntity.js';
import type {
  IProjectRepository,
  ProjectCreateInput,
  ProjectListOptions,
  ProjectUpdateInput,
} from '../../domain/repositories/IProjectRepository.js';
import { ProjectRemoteDataSource } from '../datasources/ProjectRemoteDataSource.js';
import type { ProjectDto } from '../dtos/ProjectDto.js';

function mapDtoToEntity(dto: ProjectDto): ProjectEntity {
  return {
    id: dto.id,
    orgId: dto.orgId,
    slug: dto.slug,
    name: dto.name,
    code: dto.code,
    statusMetadataId: dto.statusMetadataId ?? null,
    description: dto.description ?? null,
    startAt: dto.startAt ?? null,
    endAt: dto.endAt ?? null,
    budget: dto.budget ?? null,
    targetSessions: dto.targetSessions ?? null,
    clientMetadataId: dto.clientMetadataId ?? null,
    brandMetadataId: dto.brandMetadataId ?? null,
    categoryMetadataId: dto.categoryMetadataId ?? null,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    createdBy: dto.createdBy,
    status: dto.status ?? null,
    client: dto.client ?? null,
    brand: dto.brand ?? null,
    category: dto.category ?? null,
    kpisCount: dto.kpisCount ?? 0,
  };
}

export class ProjectRepository implements IProjectRepository {
  constructor(private readonly remoteDataSource: ProjectRemoteDataSource) {}

  async getById(id: string): Promise<ProjectEntity | null> {
    const dto = await this.remoteDataSource.getProjectById(id);
    return dto ? mapDtoToEntity(dto) : null;
  }

  async list(options?: ProjectListOptions): Promise<{ data: ProjectEntity[]; total: number }> {
    const { data, total } = await this.remoteDataSource.listProjects(options);
    return { data: data.map(mapDtoToEntity), total };
  }

  async create(input: ProjectCreateInput): Promise<ProjectEntity> {
    const dto = await this.remoteDataSource.createProject({
      orgId: input.orgId,
      slug: input.slug,
      name: input.name,
      code: input.code,
      createdBy: input.createdBy,
      description: input.description ?? null,
      statusMetadataId: input.statusMetadataId ?? null,
      startAt: input.startAt ?? null,
      endAt: input.endAt ?? null,
      budget: input.budget ?? null,
      targetSessions: input.targetSessions ?? null,
      clientMetadataId: input.clientMetadataId ?? null,
      brandMetadataId: input.brandMetadataId ?? null,
      categoryMetadataId: input.categoryMetadataId ?? null,
    });
    if (!dto) throw new Error('Project creation failed');
    return mapDtoToEntity(dto);
  }

  async update(id: string, input: ProjectUpdateInput): Promise<ProjectEntity | null> {
    const dto = await this.remoteDataSource.updateProject(id, input);
    return dto ? mapDtoToEntity(dto) : null;
  }

  async delete(id: string): Promise<void> {
    await this.remoteDataSource.deleteProject(id);
  }

  async searchProjects(orgId: string, query: string, limit?: number): Promise<ProjectEntity[]> {
    const dtos = await this.remoteDataSource.searchProjects(orgId, query, limit);
    return dtos.map(mapDtoToEntity);
  }

  async existsBySlug(orgId: string, slug: string, excludeId?: string): Promise<boolean> {
    return this.remoteDataSource.existsBySlug(orgId, slug, excludeId);
  }

  async existsByCode(orgId: string, code: string, excludeId?: string): Promise<boolean> {
    return this.remoteDataSource.existsByCode(orgId, code, excludeId);
  }
}
